// BillingAddressManager.tsx
import React, { useState, useEffect } from "react";
import { Button, Card, Col, Form, InputGroup, Modal, Row, Spinner, Badge } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import select2LangPropTypes from "../../PropTypes/select2LangPropTypes";
import {
  AddBillingAddressService,
  deleteBillingAddressService,
  getBillingAddressService,
  getCountryListService,
  makeDefaultBillingAddressService,
  UpdateBillingAddressService,
} from "../../Services/BillingAddressService";
import { useTheme } from "../../Context/ThemeContext";
import darkSelectStyles from "../../Helpers/darkSelectStyles";
import "../../Assets/scss/components/BillingAddressManager.scss";

interface BillingAddressManagerProps {
  disabledButton?: boolean;
}

interface BillingAddressItem {
  id: number;
  name: string;
  address: string;
  country: string;
  pincode: number;
  vat_tax?: string;
  is_default: number;
}

const BillingAddressManager: React.FC<BillingAddressManagerProps> = ({ disabledButton = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();

  // Redux Data
  const { accessToken } = useSelector((store: StoreProptypes) => store);

  // State
  const [billingAddressListData, setBillingAddressListData] = useState<BillingAddressItem[]>();
  const [loadingBillingAddressList, setLoadingBillingAddressList] = useState(false);
  const [showBillingAddressPopup, setShowBillingAddressPopup] = useState(false);
  const [showDeleteBillingAddressPopup, setDeleteBillingAddressPopup] = useState(false);
  const [showDeleteCardPopupLoading, setShowDeleteCardPopupLoading] = useState(false);
  const [deleteBillingAddressID, setDeleteBillingAddressID] = useState<number | null>();
  const [editingAddress, setEditingAddress] = useState<BillingAddressItem | null>(null);
  const [countryList, setCountryList] = useState<select2LangPropTypes[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form State
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [country, setCountry] = useState<select2LangPropTypes>();
  const [pincode, setPincode] = useState("");
  const [vat, setVat] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Load billing addresses on mount
  useEffect(() => {
    if (accessToken) {
      getBillingAddressService(
        accessToken,
        setBillingAddressListData,
        setLoadingBillingAddressList,
        dispatch,
        t
      );
    }
  }, [accessToken, dispatch, t]);

  // Load countries on mount
  useEffect(() => {
    if (accessToken) {
      getCountryListService(accessToken, setCountryList);
    }
  }, [accessToken]);

  // Reset form fields
  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setStreetAddress("");
    setCountry(undefined);
    setPincode("");
    setVat("");
    setEditingAddress(null);
    setErrors({});
  };

  // Split full name into first and last name
  const splitName = (fullName: string): { firstName: string; lastName: string } => {
    const parts = fullName.trim().split(" ").filter(Boolean);
    return {
      firstName: parts[0] || "",
      lastName: parts.length > 1 ? parts.slice(1).join(" ") : "",
    };
  };

  // Handle edit billing address
  const handleEditBillingAddress = (id: number) => {
    const getSelectedIdData = billingAddressListData?.find((d) => d.id === id);
    if (getSelectedIdData) {
      const { firstName, lastName } = splitName(getSelectedIdData.name);
      setFirstName(firstName);
      setLastName(lastName);
      setStreetAddress(getSelectedIdData.address);
      setCountry(countryList.find((d) => d.value === getSelectedIdData.country));
      setPincode(getSelectedIdData.pincode.toString());
      setVat(getSelectedIdData.vat_tax || "");
      setEditingAddress(getSelectedIdData);
      setShowBillingAddressPopup(true);
    }
  };

  // Handle delete billing address
  const handleDeleteBillingAddress = () => {
    if (accessToken && deleteBillingAddressID) {
      deleteBillingAddressService(
        accessToken,
        deleteBillingAddressID,
        setShowDeleteCardPopupLoading,
        setBillingAddressListData,
        setLoadingBillingAddressList,
        setDeleteBillingAddressPopup,
        dispatch,
        t
      );
    }
  };

  // Handle make default billing address
  const handleShowMakeDefaultBillingAddressPopup = (id: number) => {
    if (accessToken && id) {
      makeDefaultBillingAddressService(
        accessToken,
        id,
        setBillingAddressListData,
        setLoadingBillingAddressList,
        dispatch,
        t
      );
    }
  };

  // Handle show delete popup
  const handleShowDeleteBillingAddressPopup = (id: number) => {
    setDeleteBillingAddressPopup(true);
    id && setDeleteBillingAddressID(id);
  };

  // Handle close delete popup
  const handleCloseDeleteBillingAddressPopup = () => {
    setDeleteBillingAddressPopup(false);
    setDeleteBillingAddressID(null);
  };

  // Handle show add popup
  const handleShowBillingAddressPopup = () => {
    resetForm();
    setShowBillingAddressPopup(true);
  };

  // Handle close add/edit popup
  const handleCloseBillingAddressPopup = () => {
    setShowBillingAddressPopup(false);
    resetForm();
  };

  // Validate form
  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!firstName.trim()) {
      newErrors.firstName = t("First Name is required");
    } else if (!/^[A-Za-z0-9\s'-]+$/.test(firstName)) {
      newErrors.firstName = t("First Name can only contain letters");
    } else if (firstName.length > 100) {
      newErrors.firstName = t("First Name cannot exceed 100 characters");
    }

    if (!lastName.trim()) {
      newErrors.lastName = t("Last Name is required");
    } else if (!/^[A-Za-z0-9\s'-]+$/.test(lastName)) {
      newErrors.lastName = t("Last Name can only contain letters");
    } else if (lastName.length > 100) {
      newErrors.lastName = t("Last Name cannot exceed 100 characters");
    }

    if (!streetAddress.trim()) {
      newErrors.streetAddress = t("Street Address is required");
    } else if (!/^[A-Za-z0-9,\s'-]+$/.test(streetAddress)) {
      newErrors.streetAddress = t("Street Address can only contain letters");
    } else if (streetAddress.length > 255) {
      newErrors.streetAddress = t("Street Address cannot exceed 255 characters");
    }

    if (!country || typeof country.value !== "string" || !country.value?.trim()) {
      newErrors.country = t("Country is required");
    }

    if (!String(pincode).trim()) {
      newErrors.pincode = t("Pincode is required");
    } else if (!/^[A-Za-z0-9]+$/.test(String(pincode))) {
      newErrors.pincode = t("Pincode can only contain letters and numbers");
    } else if (pincode.length < 4 || pincode.length > 10) {
      newErrors.pincode = t("Pincode must be 4–10 digits");
    }

    if (vat?.trim()) {
      if (!/^[A-Za-z0-9]+$/.test(vat)) {
        newErrors.vat = t("Vat can only contain letters and numbers");
      } else if (vat.length > 16) {
        newErrors.vat = t("Vat cannot exceed 16 characters");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save or update billing address
  const saveBillingAddress = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!accessToken || !validate()) return;
    
    setIsSubmitting(true);
    
    try {
      const fullName = `${firstName} ${lastName}`.trim();
      
      if (editingAddress) {
        await UpdateBillingAddressService(
          editingAddress.id,
          firstName,
          lastName,
          streetAddress,
          country ? country?.value : "",
          pincode,
          accessToken,
          vat,
          setBillingAddressListData,
          setLoadingBillingAddressList,
          setShowBillingAddressPopup,
          dispatch,
          t,
          resetForm
        );
      } else {
        await AddBillingAddressService(
          firstName,
          lastName,
          streetAddress,
          country ? country?.value : "",
          pincode,
          accessToken,
          vat,
          setBillingAddressListData,
          setLoadingBillingAddressList,
          setShowBillingAddressPopup,
          dispatch,
          t,
          resetForm
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format address for display
  const formatAddress = (addressItem: BillingAddressItem): string => {
    const countryLabel = countryList.find((d) => d.value === addressItem.country)?.label || addressItem.country;
    const formattedAddress = addressItem.address.endsWith(",")
      ? addressItem.address.slice(0, -1)
      : addressItem.address;
    return `${formattedAddress}, ${countryLabel}, ${addressItem.pincode}`;
  };

  return (
    <>
      {/* Billing Address Header */}
      <div className="billing-address-header mb-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <h3 className="mb-1">{t("Billing Address")}</h3>
            <p className="text-muted mb-0">
              {t("Manage your billing addresses for invoices and subscriptions")}
            </p>
          </div>
          <Button
            variant="primary"
            onClick={handleShowBillingAddressPopup}
            disabled={disabledButton}
            className="d-flex align-items-center gap-2 px-4 py-2"
          >
            <i className="material-symbols-outlined" style={{ fontSize: "20px" }}>
              add_home
            </i>
            <span>{t("New Billing Address")}</span>
          </Button>
        </div>
      </div>

      {/* Loading State */}
      {loadingBillingAddressList && (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">{t("Loading your addresses...")}</p>
        </div>
      )}

      {/* Address Cards Grid */}
      {!loadingBillingAddressList && billingAddressListData && (
        <>
          {billingAddressListData.length > 0 ? (
            <div className="billing-address-grid">
              <Row className="g-4">
                {billingAddressListData.map((addressItem) => (
                  <Col key={addressItem.id} lg={6} xl={4}>
                    <div className={`address-card ${addressItem.is_default === 1 ? "default" : ""}`}>
                      {addressItem.is_default === 1 && (
                        <div className="default-badge">
                          <Badge bg="success" className="px-3 py-2">
                            <i className="material-symbols-outlined me-1" style={{ fontSize: "14px" }}>
                              check
                            </i>
                            {t("Primary Address")}
                          </Badge>
                        </div>
                      )}
                      
                      <div className="address-card-content">
                        <div className="address-header d-flex align-items-center justify-content-between">
                          <div className="address-icon">
                            <i className="material-symbols-outlined">home</i>
                          </div>
                          <div className="address-name">
                            <h4>{addressItem.name}</h4>
                            {addressItem.vat_tax && (
                              <span className="vat-badge">
                                {t("VAT")}: {addressItem.vat_tax}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="address-details">
                          <p className="mb-1">{formatAddress(addressItem)}</p>
                        </div>
                      </div>

                      <div className="address-actions">
                        {addressItem.is_default !== 1 ? (
                          <>
                            <Button
                              variant="link"
                              onClick={() => handleShowMakeDefaultBillingAddressPopup(addressItem.id)}
                              className="action-link"
                            >
                              {t("Make This Address Primary")}
                            </Button>
                            <div className="action-buttons-group">
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleEditBillingAddress(addressItem.id)}
                                className="action-icon-btn"
                                aria-label={t("Edit Billing Address").toString()}
                              >
                                <i className="material-symbols-outlined">edit</i>
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleShowDeleteBillingAddressPopup(addressItem.id)}
                                className="action-icon-btn"
                                aria-label={t("Delete Billing Address").toString()}
                              >
                                <i className="material-symbols-outlined">delete</i>
                              </Button>
                            </div>
                          </>
                        ) : (
                          <>
                            <strong className="text-primary me-2">
                              {t("Primary Address")}
                            </strong>
                            <div className="action-buttons-group">
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleEditBillingAddress(addressItem.id)}
                                className="action-icon-btn"
                                aria-label={t("Edit Billing Address").toString()}
                              >
                                <i className="material-symbols-outlined">edit</i>
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          ) : (
            // Empty State
            <div className="empty-state text-center py-5">
              <div className="empty-state-icon mb-4">
                <i className="material-symbols-outlined" style={{ fontSize: "80px" }}>
                  location_off
                </i>
              </div>
              <h4 className="mb-2">{t("No Billing Address Found")}</h4>
              <p className="text-muted mb-4">
                {t("Add your first billing address to manage invoices and subscriptions")}
              </p>
              <Button
                variant="primary"
                onClick={handleShowBillingAddressPopup}
                disabled={disabledButton}
                className="px-4"
              >
                <i className="material-symbols-outlined me-2">add_home</i>
                {t("Add New Billing Address")}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Billing Address Modal */}
      <Modal
        show={showBillingAddressPopup}
        onHide={handleCloseBillingAddressPopup}
        size="lg"
        backdrop="static"
        centered
        className="billing-address-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingAddress ? t("Update Billing Address") : t("Add New Billing Address")}
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body>
          <Form onSubmit={saveBillingAddress}>
            <Row>
              <Col md={6}>
                <InputGroup className="mt-3 input-group-outline">
                  <Form.Control
                    placeholder={t("First Name")}
                    aria-label={t("First Name")}
                    type="text"
                    className="rounded-2"
                    size="lg"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                    value={firstName}
                    isInvalid={!!errors.firstName}
                    style={{ backgroundImage: "none", paddingRight: "0.75rem" }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.firstName}
                  </Form.Control.Feedback>
                </InputGroup>
              </Col>
              <Col md={6}>
                <InputGroup className="mt-3 input-group-outline">
                  <Form.Control
                    placeholder={t("Last Name")}
                    aria-label={t("Last Name")}
                    type="text"
                    className="rounded-2"
                    size="lg"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                    value={lastName}
                    isInvalid={!!errors.lastName}
                    style={{ backgroundImage: "none", paddingRight: "0.75rem" }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.lastName}
                  </Form.Control.Feedback>
                </InputGroup>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <InputGroup className="mt-3 input-group-outline">
                  <Form.Control
                    placeholder={t("Street Address")}
                    aria-label={t("Street Address")}
                    type="text"
                    className="rounded-2"
                    size="lg"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStreetAddress(e.target.value)}
                    value={streetAddress}
                    isInvalid={!!errors.streetAddress}
                    style={{ backgroundImage: "none", paddingRight: "0.75rem" }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.streetAddress}
                  </Form.Control.Feedback>
                </InputGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6} className="mt-3">
                <InputGroup className="h-100 input-group-outline">
                  <Select
                    id="language_select"
                    onChange={(e) => { 
                      setCountry({ 
                        label: e ? e.label : "", 
                        value: e ? String(e.value) : "" 
                      }) 
                    }}
                    options={countryList}
                    defaultValue={country}
                    value={country}
                    closeMenuOnSelect={true}
                    backspaceRemovesValue={true}
                    isClearable={false}
                    isSearchable={true}
                    placeholder={`${t('Please select country')}...`}
                    className={`aioa_dashboard-custom-country-select w-100 rounded-2 ${errors.country ? "is-invalid" : ""}`}
                    aria-label={t('Select Country')}
                    styles={theme.theme === 'dark' ? darkSelectStyles : {}}
                    classNamePrefix="lang-select"
                    theme={(selectTheme) => ({
                      ...selectTheme,
                      borderRadius: 8,
                      colors: {
                        ...selectTheme.colors,
                        primary25: '#d9cce6',
                        primary: '#420083',
                      },
                    })}
                  />
                  {errors.country && (
                    <div className="invalid-feedback d-block">{errors.country}</div>
                  )}
                </InputGroup>
              </Col>
              <Col md={6}>
                <InputGroup className="mt-3 input-group-outline">
                  <Form.Control
                    placeholder={t("Pincode")}
                    aria-label={t("Pincode")}
                    type="text"
                    className="rounded-2"
                    size="lg"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPincode(e.target.value)}
                    value={pincode}
                    isInvalid={!!errors.pincode}
                    style={{ backgroundImage: "none", paddingRight: "0.75rem" }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.pincode}
                  </Form.Control.Feedback>
                </InputGroup>
              </Col>
            </Row>

            <Row>
              <Col md={12} className="mt-3">
                <InputGroup className="input-group-outline">
                  <Form.Control
                    placeholder={t("VAT Number")}
                    aria-label={t("VAT Number")}
                    type="text"
                    className="rounded-2"
                    size="lg"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVat(e.target.value)}
                    value={vat}
                    isInvalid={!!errors.vat}
                    style={{ backgroundImage: "none", paddingRight: "0.75rem" }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.vat}
                  </Form.Control.Feedback>
                </InputGroup>
              </Col>
            </Row>

            <div className="card-save-btns d-flex m-2 justify-content-center mt-4">
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Spinner as="span" animation="border" size="sm" />
                ) : editingAddress ? (
                  <span>{t("Update")}</span>
                ) : (
                  <span>{t("Save Button Label")}</span>
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteBillingAddressPopup}
        onHide={handleCloseDeleteBillingAddressPopup}
        size="lg"
        backdrop="static"
        centered
        className="delete-confirmation-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("Delete Billing Address")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("Are you sure want to delete billing address?")}</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleDeleteBillingAddress} disabled={showDeleteCardPopupLoading}>
            {showDeleteCardPopupLoading ? (
              <Spinner as="span" animation="border" size="sm" />
            ) : (
              t("Delete Button Label")
            )}
          </Button>
          <Button variant="danger" onClick={handleCloseDeleteBillingAddressPopup}>
            {t("Close")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default BillingAddressManager;